import React, { useEffect, useState } from 'react';
import { valueNeedsScore } from '@Components/RiskSetup/templates/countValuesNeedingScore';
import { useDispatch } from 'react-redux';
import { useUnits, DataGrid, useGridApiRef } from '@innovyze/stylovyze';
import {
	selectDataGridState,
	selectRiskScoreDataGrid,
	selectRiskScores,
	selectRiskUnitScores,
} from '@Selectors';
import {
	setDataGridState,
	setRiskEditScores,
	setRiskScoreDataGrid,
	setRiskScores,
	setRiskUnitScores,
	updateRiskScoreDataGrid,
	updateRiskValuesDataGrid,
} from '@Actions';
import { tableColumns } from './TabelColumns';
import { TableToolbar, TableToolbarUnique } from './TableToolbar';
import { RiskValueChange } from '@Reducers/util.reducer';
import { ScoreDataGridRow } from '@Types/scoreDataGridType';
import { ComponentCountReply } from '@Types/risk.types';
import { useGlobalization } from '@Translations/useGlobalization';
import { NotificationBanner } from '@Components/NotificationBanner/NotificationBanner.component';

export const updateRiskCounts = (
	scoreRows: ScoreDataGridRow[],
	riskCounts: ComponentCountReply[] | undefined,
	isMetric: boolean,
	isUnique: boolean,
): ScoreDataGridRow[] => {
	if (riskCounts === undefined || riskCounts.length == 0) {
		const newScoreRows: ScoreDataGridRow[] = [];
		for (let i = 0; i < scoreRows.length; i++) {
			newScoreRows.push({
				...scoreRows[i],
				count: undefined,
				length: undefined,
				countPercent: undefined,
				lengthPercent: undefined,
			});
		}
		return newScoreRows;
	} else {
		const conversion = isMetric ? 1 : 0.06213712;
		const newScoreRows: ScoreDataGridRow[] = [];
		if (isUnique) {
			for (let i = 0; i < scoreRows.length; i++) {
				const count = riskCounts.find(
					({ key }) =>
						(key === undefined || key === null
							? '--'
							: key.toString()) === scoreRows[i].value.toString(),
				);

				if (count) {
					newScoreRows.push({
						...scoreRows[i],
						count: count.count,
						length: Math.round(count.length * conversion) / 100,
						countPercent:
							Math.round(count.count_percent * 1000) / 10,
						lengthPercent:
							Math.round(count.length_percent * 1000) / 10,
					});
				} else {
					newScoreRows.push({
						...scoreRows[i],
						count: 0,
						length: 0,
						countPercent: 0,
						lengthPercent: 0,
					});
				}
			}
		} else if (riskCounts) {
			for (let i = 0; i < scoreRows.length; i++) {
				newScoreRows.push({
					...scoreRows[i],
					count: riskCounts[i].count,
					length: Math.round(riskCounts[i].length * conversion) / 100,
					countPercent:
						Math.round(riskCounts[i].count_percent * 1000) / 10,
					lengthPercent:
						Math.round(riskCounts[i].length_percent * 1000) / 10,
				});
			}
		} else {
			for (let i = 0; i < scoreRows.length; i++) {
				newScoreRows.push({
					...scoreRows[i],
					count: 0,
					length: 0,
					countPercent: 0,
					lengthPercent: 0,
				});
			}
		}

		return newScoreRows;
	}
};

interface ScoreDataGridProps {
	forComponent: boolean;
	forInspection: boolean;
	isLoading: boolean;
	isLoadingCount: boolean;
	dataGridKey: string;
	countKey: string;
	isUnique: boolean;
	isNew: boolean;
	rangeClasses: number;
	distance?: number;
	unit?: string;
	currency?: string;
	precision?: number;
	hideCounts?: boolean;
	isDeteriorationModel?: boolean;
	// Values the applied template could not match for this component; rows
	// with one of these values still scored 0 are highlighted for the user.
	unrecognisedValues: string[];
	templateLabel: string | undefined;
	// Row highlight + unmatched-values banner are risk-templates guidance:
	// off with the flag off and on multi-param sub-screens, where there is no
	// template to have failed to match against.
	highlightUnscored: boolean;
	onSetRangeClasses: (classes: number) => void;
	onRangeValuesChanged: () => void;
	onRecalculateCount: () => void;
}

// This ensure we remove any extranious decimal places caused by javascript
const toFixedDP = (value: number, precision: number): number => {
	return +value.toFixed(precision);
};

const ScoreDataGrid = ({
	forComponent,
	forInspection,
	isLoading,
	isLoadingCount,
	dataGridKey,
	countKey,
	isUnique,
	rangeClasses,
	distance,
	unit,
	currency,
	precision,
	hideCounts,
	isDeteriorationModel,
	unrecognisedValues,
	templateLabel,
	highlightUnscored,
	onSetRangeClasses,
	onRangeValuesChanged,
	onRecalculateCount,
}: ScoreDataGridProps) => {
	const dispatch = useDispatch();
	const { t } = useGlobalization();
	const { getSystemUnit, getStandardisedValue, system } = useUnits();
	const [currentDistance, setCurrentDistance] = useState<number | undefined>(
		distance,
	);

	const apiRef = useGridApiRef();
	const dataGridState = selectDataGridState(dataGridKey);

	const scoreRows = selectRiskScoreDataGrid();
	const riskScore = selectRiskScores();
	const riskUnitScore = selectRiskUnitScores();

	const scoreMax = 1000;

	// A null score is a durable, authoritative "this needs scoring" signal —
	// it's real persisted data (per I360A-3171, the backend fix for
	// unrecognised values never reaching this grid), so it must flag on its
	// own, with no dependency on `unrecognisedValues` (session-scoped Redux
	// state from the most recent apply — empty on any later page load/reopen,
	// even when the underlying data still has null scores).
	// A score of 0 is ambiguous (a user can legitimately assign 0), so until
	// every unmatched value ships as null it still needs the cross-check
	// against `unrecognisedValues` to avoid flagging a deliberate 0.
	const needsScore = (row: ScoreDataGridRow): boolean =>
		highlightUnscored &&
		valueNeedsScore(row.score, row.value, unrecognisedValues);
	// Recomputed from the live rows, so it drops as the user scores them.
	const unmatchedCount = scoreRows.filter(needsScore).length;

	useEffect(() => {
		if (distance !== currentDistance) {
			setCurrentDistance(distance);
			if (scoreRows[0].count !== undefined) {
				onRangeValuesChanged();
				const newScoreRows = updateRiskCounts(
					scoreRows,
					undefined,
					system === 'Metric',
					isUnique,
				);
				dispatch(setRiskScoreDataGrid(newScoreRows));
			}
		}
	}, [distance]);

	const onScoreChange = (index: number, value: number) => {
		dispatch(updateRiskScoreDataGrid({ index, score: value }));
	};

	const onRangeChange = (
		index: number,
		value: number,
		valueUnit?: number,
	) => {
		if (riskScore[index] !== value) {
			onRangeValuesChanged();

			// Clear the existing counts so they have to manual request them
			if (scoreRows[0].count !== undefined) {
				const newScoreRows = updateRiskCounts(
					scoreRows,
					undefined,
					system === 'Metric',
					isUnique,
				);
				dispatch(setRiskScoreDataGrid(newScoreRows));
			}

			const decimalPt = precision !== undefined ? precision : 1;
			const minDiff = toFixedDP(1 / Math.pow(10, decimalPt), decimalPt);

			let workingValue = value;
			let newScore = [...riskScore];

			// We need to work out if we need do this next bit in native or system units
			if (
				system === 'Imperial' &&
				riskUnitScore &&
				unit &&
				valueUnit != undefined
			) {
				workingValue = valueUnit;
				newScore = [...riskUnitScore];
			}

			// Has the user decremented or incremented the value?
			if (workingValue < newScore[index]) {
				newScore[index] = workingValue;
				// Decrementing so we only need to worry about the items BEFORE this index and if it's the first item we dont need to do anything at all
				if (index !== 0) {
					// Have we overlapped the previous value?
					let nextValue = workingValue;
					for (let i = index - 1; i >= 0; i--) {
						if (newScore[i] >= nextValue) {
							nextValue = toFixedDP(
								nextValue - minDiff,
								decimalPt,
							);
							newScore[i] = nextValue;
						}
					}
				}
			} else {
				newScore[index] = workingValue;
				// Incremeting so we only need to worry about the items AFTER this index and if it's the last item we just need to update the last entry
				if (index == newScore.length - 2)
					newScore[index + 1] = workingValue;
				else {
					// Have we overlapped the next value?
					let nextValue = workingValue;
					for (let i = index + 1; i <= newScore.length - 2; i++) {
						if (newScore[i] <= nextValue) {
							nextValue = toFixedDP(
								nextValue + minDiff,
								decimalPt,
							);
							newScore[i] = nextValue;
						}
					}
				}
			}

			// Ensure the last two values are the same
			newScore[newScore.length - 1] = newScore[newScore.length - 2];

			// Are we running a different display unit?
			if (
				system === 'Imperial' &&
				riskUnitScore &&
				unit &&
				valueUnit != undefined
			) {
				const internalUnit = getSystemUnit(unit);
				let adjustment = 1;
				if (valueUnit !== undefined && value != valueUnit) {
					// mm -> in -> cm
					if (unit === 'mm') adjustment = 10;
				}

				const nativeScores = newScore.map(score => {
					return (
						parseFloat(
							getStandardisedValue(`${score} ${internalUnit}`),
						) * adjustment
					);
				});
				const stringScores = nativeScores.map(score => {
					return score.toString();
				});
				dispatch(setRiskEditScores(stringScores));
				dispatch(setRiskScores(nativeScores));
				dispatch(setRiskUnitScores(newScore));

				// Now switch back to the native scores for the next bit as thats in native units
				newScore = nativeScores;
			} else {
				const stringScores = newScore.map(score => {
					return score.toString();
				});
				dispatch(setRiskEditScores(stringScores));
				dispatch(setRiskScores(newScore));
			}

			const newValues: RiskValueChange[] = [];
			for (let i = 0; i < newScore.length; i++) {
				if (i == 0) {
					newValues.push({
						index: i,
						value: ` <= ${newScore[0]}`,
					});
				} else if (i < newScore.length - 1) {
					newValues.push({
						index: i,
						value: `${newScore[i - 1]} <= ${newScore[i]}`,
					});
				} else {
					newValues.push({
						index: i,
						value: `> ${newScore[i]}`,
					});
				}
			}

			dispatch(updateRiskValuesDataGrid(newValues));
		}
	};

	const onStateChange = React.useCallback(() => {
		// We dont want to use the individual states that could be passed but just export the whole state to keep it all in sync
		const state = apiRef.current.exportState();
		dispatch(
			setDataGridState({
				key: dataGridKey,
				initialState: state,
			}),
		);
	}, [dataGridKey]);

	return (
		<>
			{unmatchedCount > 0 && (
				<NotificationBanner
					variant="warning"
					sx={{ mb: 2 }}
					dataCy="unmatched-warning">
					<b>
						{/* Separate label-free sentence so a missing label can't
						    produce "to the template template." */}
						{templateLabel
							? t(
									'We could not match {{count}} of your values to the {{label}} template.',
									{
										count: unmatchedCount,
										label: templateLabel,
									},
							  )
							: t(
									'We could not match {{count}} of your values to the template.',
									{ count: unmatchedCount },
							  )}
					</b>{' '}
					{t(
						'Give each one a score below (highlighted) so nothing is left unscored.',
					)}
				</NotificationBanner>
			)}
			<DataGrid
				apiRef={apiRef}
				disableColumnMenu
				disableColumnReorder
				loading={isLoading}
				skeletonRowCount={
					rangeClasses + (isUnique ? 0 : forInspection ? 2 : 3)
				}
				columns={tableColumns(
					forComponent,
					onScoreChange,
					onRangeChange,
					isUnique,
					countKey,
					scoreMax,
					unit,
					currency,
					precision,
					dataGridState,
					hideCounts,
					isDeteriorationModel,
				)}
				rows={scoreRows}
				getRowClassName={params =>
					needsScore(params.row as ScoreDataGridRow)
						? 'row-needs-score'
						: ''
				}
				sx={{
					// Unmatched, unscored values: row tint plus an inset left accent
					// (box-shadow, so it doesn't shift the layout).
					'& .row-needs-score': {
						background: 'rgba(250, 162, 27, 0.06)',
						boxShadow: 'inset 3px 0 0 #FAA21B',
					},
					'& .row-needs-score:hover': {
						background: 'rgba(250, 162, 27, 0.1)',
					},
				}}
				slots={{
					toolbar:
						isUnique && currentDistance === undefined
							? TableToolbarUnique
							: TableToolbar,
				}}
				slotProps={{
					toolbar: {
						isLoading: isLoading || isLoadingCount,
						isUnique: isUnique,
						hideCounts: hideCounts,
						onSetRangeClasses: onSetRangeClasses,
						onRecalculateCount: onRecalculateCount,
						rangeClasses: rangeClasses,
						recalculateOnly: isUnique,
						forComponent: forComponent,
					},
				}}
				// DONT USE onStateChange={onStateChange} AS THIS GETS CALLED FOR EVERY USER ACTION... SO PROBABLY BEST TO AVOID !!
				onColumnWidthChange={onStateChange}
				dataCy="score-table"
			/>
		</>
	);
};

export default ScoreDataGrid;
